import { useCallback, useEffect, useState } from 'react';
import type { AuthUser } from '@/providers/AuthProvider';

export const ONBOARDING_STEPS = ['email', 'profile', 'cnpj'] as const;
export type OnboardingStep = (typeof ONBOARDING_STEPS)[number];

export interface WizardState {
  currentStep: OnboardingStep;
  completed: Partial<Record<OnboardingStep, true>>;
  profile?: {
    name: string;
    crm: string;
    specialty?: string;
    phoneE164: string;
  };
  cnpjId?: string;
  cnpjSummary?: {
    cnpj: string;
    razaoSocial: string;
  };
}

const STORAGE_KEY = 'organizaai.onboarding.v1';

const INITIAL: WizardState = {
  currentStep: 'email',
  completed: {},
};

function read(): WizardState {
  if (typeof window === 'undefined') return INITIAL;
  try {
    const raw = window.sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return INITIAL;
    const parsed = JSON.parse(raw) as WizardState;
    const merged: WizardState = {
      ...INITIAL,
      ...parsed,
      completed: parsed.completed ?? {},
    };
    // Fallback para sessões antigas que tinham 'certificate' ou 'service' como currentStep.
    if (!ONBOARDING_STEPS.includes(merged.currentStep)) {
      merged.currentStep = 'cnpj';
    }
    return merged;
  } catch {
    return INITIAL;
  }
}

export function useWizardState() {
  const [state, setState] = useState<WizardState>(read);

  useEffect(() => {
    try {
      window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(state));
    } catch {
      // storage disabled — proceed without persistence
    }
  }, [state]);

  const update = useCallback((patch: Partial<WizardState>) => {
    setState((prev) => ({ ...prev, ...patch }));
  }, []);

  const markCompleted = useCallback((step: OnboardingStep) => {
    setState((prev) => ({ ...prev, completed: { ...prev.completed, [step]: true } }));
  }, []);

  const goTo = useCallback((step: OnboardingStep) => {
    setState((prev) => ({ ...prev, currentStep: step }));
  }, []);

  const reset = useCallback(() => {
    setState(INITIAL);
    try {
      window.sessionStorage.removeItem(STORAGE_KEY);
    } catch {
      // noop
    }
  }, []);

  return { state, update, markCompleted, goTo, reset };
}

export function stepIndex(step: OnboardingStep): number {
  return ONBOARDING_STEPS.indexOf(step);
}

export function deriveCompletedFromUser(
  user: AuthUser,
): Partial<Record<OnboardingStep, true>> {
  const completed: Partial<Record<OnboardingStep, true>> = {};
  if (user.emailVerifiedAt) completed.email = true;
  if (user.hasProfile) completed.profile = true;
  if (user.hasCnpj) completed.cnpj = true;
  return completed;
}

export function firstIncompleteStep(
  completed: Partial<Record<OnboardingStep, true>>,
): OnboardingStep {
  return ONBOARDING_STEPS.find((s) => !completed[s]) ?? ONBOARDING_STEPS[ONBOARDING_STEPS.length - 1];
}
